import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { findProductBySlug, getProductSlug, getProductTypeLabel, getProductPageTitle, getProductPageDescription, SITE_URL } from "@/utils/seo";
import { trackViewItem } from "@/utils/ga4";
import { API, BACKEND_URL } from "@/config";
import { ArrowRight, CheckCircle, ChevronLeft } from "lucide-react";

const SITE = SITE_URL;

const imgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const yesNo = (v) => (v === true || v === "yes") ? "Yes" : v === false ? "No" : v ?? "—";

const SpecRow = ({ label, value }) =>
  value && value !== "—" ? (
    <tr className="border-b border-[#FFD166]/8">
      <td className="py-2.5 text-[#B9C8D5] text-sm pr-6 w-44">{label}</td>
      <td className="py-2.5 text-[#F6F1E4] text-sm font-medium">{value}</td>
    </tr>
  ) : null;

const foldLabel = (v) => {
  if (!v) return null;
  const map = { very_easy: "Very easy", easy: "Easy", moderate: "Moderate", difficult: "Difficult" };
  return map[v] || v;
};

const reclineLabel = (v) => {
  if (!v) return null;
  const map = { full: "Full (flat)", partial: "Partial", minimal: "Minimal", none: "None" };
  return map[v] || v;
};

export default function StrollerProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]); // for bundle component name lookup
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  // Guard against React StrictMode double-invocation and slug changes
  const viewFiredRef = useRef(null); // tracks which slug's view_item has been sent

  useEffect(() => {
    // Fetch products AND bundles in parallel; check bundles when slug not in products
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/bundles`).catch(() => ({ data: { bundles: [] } })),
    ]).then(([pRes, bRes]) => {
      const all = pRes.data.products || [];
      const allBundles = (bRes.data.bundles || []).map((b) => ({ ...b, is_bundle: true }));

      // Try products first
      let p = findProductBySlug(all, slug);
      // If not found in products, check bundles by id match
      if (!p) {
        p = allBundles.find((b) => b.id === slug || (b.seo_slug && b.seo_slug === slug));
      }
      if (!p) { navigate("/strollers", { replace: true }); return; }
      setProduct(p);
      setAllProducts(all);
      // Fire view_item only once per slug (guards StrictMode double-invoke)
      if (viewFiredRef.current !== p.id) {
        viewFiredRef.current = p.id;
        trackViewItem(p);
      }
      const relIds = p.related || [];
      const relProducts = all.filter((x) => x && relIds.includes(x.id) && x.id !== p.id);
      if (relProducts.length === 0) {
        const t = (p.stroller_attributes || {}).stroller_type;
        const fallback = all.filter(
          (x) => x && x.id !== p.id && x.category === p.category &&
            (!t || (x.stroller_attributes || {}).stroller_type === t)
        ).slice(0, 3);
        setRelated(fallback);
      } else {
        setRelated(relProducts.slice(0, 3));
      }
    }).finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <>
        <Nav />
        <main className="min-h-screen pt-32 container" style={{ background: "#040A0F" }}>
          <div className="animate-pulse space-y-4 max-w-3xl">
            <div className="h-6 w-40 bg-[#0A1825] rounded" />
            <div className="h-10 w-80 bg-[#0A1825] rounded" />
            <div className="h-64 w-full bg-[#0A1825] rounded-2xl" />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!product) return null;

  const sa = product.stroller_attributes || {};
  const img = imgSrc(product.image);
  const isBundle = !!product.is_bundle;
  // isStroller is true only for regular (non-bundle) stroller products
  const isStroller = !isBundle && product.category === "strollers";
  // isStrollerPage drives breadcrumb / back-link for all stroller catalog items
  const isStrollerPage = product.category === "strollers" || product.category === "bundles" || isBundle;
  // Use stored seo_slug for canonical (falls back to URL param via getProductSlug)
  const canonicalSlug = getProductSlug(product) || slug;
  const canonicalUrl = `${SITE}/strollers/${canonicalSlug}`;
  // Use admin-managed SEO fields if available; fall back to dynamic category-aware values
  const pageTitle = (product.seo_title && product.seo_title.trim())
    ? product.seo_title.trim()
    : getProductPageTitle(product);
  const pageDesc = (product.seo_description && product.seo_description.trim())
    ? product.seo_description.trim()
    : getProductPageDescription(product);
  const typeLabel = getProductTypeLabel(product);

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: pageTitle.replace(" | Enchanted Gear Rentals", ""),
    description: product.description || pageDesc,
    provider: { "@id": `${SITE}/#business` },
    areaServed: { "@type": "City", name: "Orlando" },
    ...(img ? { image: img } : {}),
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: product.tier_1_3,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: product.tier_1_3,
        priceCurrency: "USD",
        unitText: "DAY",
      },
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE}/#business` },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      ...(isStrollerPage ? [{ "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` }] : []),
      { "@type": "ListItem", position: isStrollerPage ? 3 : 2, name: product.name, item: canonicalUrl },
    ],
  };

  const freeAcc = product.free_accessories || [];

  return (
    <>
      <PageSEO
        title={pageTitle}
        description={pageDesc}
        canonical={canonicalUrl}
        image={img || undefined}
        structuredData={[productSchema, breadcrumbSchema]}
      />
      <Nav />
      <main className="min-h-screen pt-28 pb-20" style={{ background: "#040A0F" }}>
        <div className="container max-w-5xl">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-[#FFD166]">Home</Link>
            <span className="opacity-40">/</span>
            {isStrollerPage ? (
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
            ) : (
              <Link to="/" className="hover:text-[#FFD166]">All Gear</Link>
            )}
            <span className="opacity-40">/</span>
            <span className="text-[#FFD166]">{product.name}</span>
          </nav>

          <div className="grid md:grid-cols-2 gap-10 mb-16">
            {/* Image */}
            <div className="rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 overflow-hidden">
              {img ? (
                <img
                  src={img}
                  alt={`${product.name} rental near Walt Disney World`}
                  className="w-full h-full object-cover"
                  width="600"
                  height="500"
                />
              ) : (
                <div className="h-80 flex items-center justify-center text-[#FFD166]/20 text-7xl">&#9885;</div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-3">{typeLabel}</span>
              <h1 className="font-serif text-3xl md:text-4xl text-[#F6F1E4] mb-2">{product.name}</h1>
              {product.tagline && <p className="text-[#B9C8D5] text-lg mb-4">{product.tagline}</p>}

              {/* Pricing */}
              <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-4 mb-5">
                <div className="eyebrow text-xs mb-2">Rental pricing</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <div className="text-[#FFD166] font-bold text-xl">${product.tier_1_3}</div>
                    <div className="text-[10px] text-[#B9C8D5]">Days 1–3</div>
                  </div>
                  {product.tier_4_7 && (
                    <div className="text-center">
                      <div className="text-[#FFD166] font-bold text-xl">${product.tier_4_7}</div>
                      <div className="text-[10px] text-[#B9C8D5]">Days 4–7</div>
                    </div>
                  )}
                  {product.tier_8_plus && (
                    <div className="text-center">
                      <div className="text-[#FFD166] font-bold text-xl">${product.tier_8_plus}</div>
                      <div className="text-[10px] text-[#B9C8D5]">Day 8+</div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[#B9C8D5] mt-2">Free delivery to Disney resorts, Universal Orlando, hotels &amp; vacation homes.</p>
              </div>

              {/* Included accessories */}
              {freeAcc.length > 0 && (
                <div className="mb-5">
                  <div className="eyebrow text-xs mb-2">Included at no extra charge</div>
                  <ul className="space-y-1">
                    {freeAcc.map((a) => (
                      <li key={a} className="flex items-center gap-2 text-sm text-[#F6F1E4]">
                        <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-auto">
                <Link to={`/?focus=${product.id}#trip-setup`}>
                  <button className="btn-primary w-full mb-3" data-testid="product-book-cta">
                    Check Availability &amp; Reserve <ArrowRight className="inline w-4 h-4 ml-1" />
                  </button>
                </Link>
                <Link to={isStrollerPage ? "/strollers" : "/"} className="flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-[#FFD166] justify-center">
                  <ChevronLeft className="w-3 h-3" /> {isStrollerPage ? "Back to all strollers" : "Back to home"}
                </Link>
              </div>
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <section className="mb-12">
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">About the {product.name}</h2>
              <p className="text-[#B9C8D5] leading-relaxed">{product.description}</p>
            </section>
          )}

          {/* What's in this bundle — only for bundles with components */}
          {isBundle && (product.components || []).length > 0 && (
            <section className="mb-12" data-testid="bundle-components-section">
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">What's in this bundle</h2>
              <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 overflow-hidden divide-y divide-[#FFD166]/8">
                {(product.components || []).map((c, i) => {
                  const comp = allProducts.find((p) => p.id === c.product_id);
                  const compImg = imgSrc(comp?.image || null);
                  const compName = c.product_name || comp?.name || c.product_id;
                  return (
                    <div key={i} className="flex items-center gap-4 p-4" data-testid={`bundle-component-row-${i}`}>
                      {compImg ? (
                        <img src={compImg} alt={compName} className="w-16 h-16 rounded-lg object-cover shrink-0 bg-[#060f18]" loading="lazy" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-[#060f18] shrink-0 flex items-center justify-center text-[#FFD166]/20 text-2xl">&#9885;</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-[#F6F1E4] font-medium">{compName}</div>
                        {comp?.tagline && <div className="text-sm text-[#B9C8D5] mt-0.5">{comp.tagline}</div>}
                      </div>
                      <div className="text-[#FFD166] font-semibold shrink-0 text-sm">× {c.quantity}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Highlights / features — shown when the product has a features list */}
          {(product.features || []).length > 0 && (
            <section className="mb-12" data-testid="product-features-section">
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Highlights</h2>
              <ul className="space-y-2">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#F6F1E4] text-sm">
                    <CheckCircle className="w-4 h-4 text-[#FFD166] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Specifications */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Specifications</h2>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 overflow-hidden">
              <table className="w-full px-6">
                <tbody className="divide-y divide-[#FFD166]/8">
                  <SpecRow label="Type" value={typeLabel} />
                  {isStroller && sa.seats && <SpecRow label="Seats" value={`${sa.seats} child${sa.seats > 1 ? "ren" : ""}`} />}
                  {isStroller && sa.max_child_weight_lbs ? <SpecRow label="Max child weight" value={`${sa.max_child_weight_lbs} lbs per seat`} /> : null}
                  {isStroller && sa.max_age_years ? <SpecRow label="Max age" value={`Up to ${sa.max_age_years} years`} /> : null}
                  {isStroller && sa.weight_lbs ? <SpecRow label="Stroller weight" value={`${sa.weight_lbs} lbs`} /> : null}
                  {isStroller && <SpecRow label="One-hand fold" value={yesNo(sa.one_hand_fold)} />}
                  {isStroller && <SpecRow label="Compact fold" value={yesNo(sa.compact_fold)} />}
                  {isStroller && <SpecRow label="Self-standing fold" value={yesNo(sa.self_standing_fold)} />}
                  {isStroller && <SpecRow label="Fold difficulty" value={foldLabel(sa.fold_difficulty)} />}
                  {isStroller && <SpecRow label="Seat recline" value={reclineLabel(sa.recline)} />}
                  {isStroller && <SpecRow label="Nap-friendly recline" value={yesNo(sa.nap_friendly)} />}
                  {isStroller && sa.canopy_coverage && <SpecRow label="Canopy coverage" value={sa.canopy_coverage} />}
                  {isStroller && <SpecRow label="Adjustable footrest" value={yesNo(sa.adjustable_footrest)} />}
                  {!isStroller && product.min_age && <SpecRow label="Age range" value={product.min_age} />}
                </tbody>
              </table>
            </div>
          </section>

          {/* Sanitization note */}
          <section className="mb-12 rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
            <h2 className="font-serif text-xl text-[#F6F1E4] mb-2">Sanitization between every rental</h2>
            <p className="text-[#B9C8D5] text-sm leading-relaxed">Every rental item is cleaned and sanitized between rentals using hospital-grade disinfectants. We inspect for wear and damage before it reaches your family.</p>
          </section>

          {/* Related */}
          {related.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-6">You might also consider</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
                {related.map((r) => {
                  const rSlug = getProductSlug(r);
                  const rImg = imgSrc(r.image);
                  return (
                    <Link key={r.id} to={`/strollers/${rSlug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 overflow-hidden transition-all">
                      {rImg && <img src={rImg} alt={r.name} className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                      <div className="p-4">
                        <div className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{r.name}</div>
                        <div className="text-[#FFD166] text-sm mt-1">From ${r.tier_1_3}/day</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
